import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  ChartLineUp,
  Spinner,
  Lightning,
  Receipt,
  Calendar,
  Download,
  TrendUp,
  TrendDown,
  Buildings,
  Users,
  CurrencyCircleDollar,
  Wallet,
  Warning,
  MagnifyingGlass,
  Funnel,
} from "@phosphor-icons/react";
import Pagination from "../components/Pagination";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const periodToDays = { day: 1, week: 7, month: 30, year: 360 };
const PIE_COLORS = ["#4F46E5", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#06B6D4", "#EC4899", "#14B8A6"];

const downloadCSV = (filename, headers, rows) => {
  const BOM = "\uFEFF";
  const csvContent = BOM + ["sep=;", headers.join(";"), ...rows.map(r => r.join(";"))].join("\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const ReportsPage = () => {
  const { user } = useAuth();
  const isTenant = user?.role === "tenant";

  const consumptionLabel = "Tüketim";
  const topupLabel = "Yükleme";
  const revenueLabel = "Gelir";

  const [consumption, setConsumption] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [staffPerformance, setStaffPerformance] = useState([]);
  const [topConsumers, setTopConsumers] = useState([]);
  const [debtData, setDebtData] = useState(null);
  const [period, setPeriod] = useState("month");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(isTenant ? "consumption" : "revenue");
  const [txPage, setTxPage] = useState(1);
  const [txPageSize, setTxPageSize] = useState(20);

  // Transaction filters
  const [txFilterType, setTxFilterType] = useState("all");
  const [txFilterMethod, setTxFilterMethod] = useState("all");
  const [txFilterSearch, setTxFilterSearch] = useState("");
  const [txFilterDateFrom, setTxFilterDateFrom] = useState("");
  const [txFilterDateTo, setTxFilterDateTo] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let days = periodToDays[period] || 30;
      let periodParam = period;
      let customDays = null;
      if (period === "custom" && customFrom && customTo) {
        const from = new Date(customFrom);
        const to = new Date(customTo);
        customDays = Math.max(1, Math.ceil((to - from) / 86400000));
        days = Math.min(customDays, 360);
        periodParam = "custom";
      }
      const cutoffDate = new Date(Date.now() - 360 * 86400000).toISOString();
      const chartDays = period === "custom" ? days : (periodToDays[period] || 30);
      const requests = [
        axios.get(`${API_URL}/api/consumption?period=${periodParam}${period === "custom" ? `&days=${days}` : ""}`),
        axios.get(`${API_URL}/api/consumption/chart?days=${chartDays}`),
        axios.get(`${API_URL}/api/transactions`),
      ];
      if (!isTenant) {
        requests.push(axios.get(`${API_URL}/api/revenue/summary`));
        requests.push(axios.get(`${API_URL}/api/staff-performance`));
        requests.push(axios.get(`${API_URL}/api/reports/top-consumers?period=${periodParam}${period === "custom" ? `&days=${days}` : ""}`));
        requests.push(axios.get(`${API_URL}/api/reports/debt-tracking`));
      }
      const results = await Promise.all(requests);
      setConsumption(results[0].data);
      const allTxns = results[2].data || [];
      // Özel tarih aralığı varsa filtrele, yoksa 360 gün sınırı
      let filteredTxns = allTxns.filter(tx => tx.created_at >= cutoffDate);
      if (period === "custom" && customFrom && customTo) {
        const fromISO = new Date(customFrom).toISOString();
        const toISO = new Date(customTo + "T23:59:59").toISOString();
        filteredTxns = allTxns.filter(tx => tx.created_at >= fromISO && tx.created_at <= toISO);
      }
      setTransactions(filteredTxns);
      const raw = results[1].data;
      setChartData(raw.allowed === false ? [] : (raw.data || []));
      if (!isTenant && results[3]) {
        setRevenue(results[3].data);
      }
      if (!isTenant && results[4]) {
        setStaffPerformance(results[4].data);
      }
      if (!isTenant && results[5]) {
        setTopConsumers(results[5].data || []);
      }
      if (!isTenant && results[6]) {
        setDebtData(results[6].data);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [period, isTenant, customFrom, customTo]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const periodLabels = {
    day: "Bugün",
    week: "Bu Hafta",
    month: "Bu Ay",
    year: "Bu Yıl",
    custom: "Özel Aralık",
  };

  // Filtered transactions
  const filteredTransactions = useMemo(() => {
    let filtered = [...transactions];
    if (txFilterType !== "all") {
      filtered = filtered.filter(tx => tx.type === txFilterType);
    }
    if (txFilterMethod !== "all") {
      filtered = filtered.filter(tx => (tx.payment_method || "") === txFilterMethod);
    }
    if (txFilterSearch.trim()) {
      const q = txFilterSearch.toLowerCase();
      filtered = filtered.filter(tx =>
        (tx.description || "").toLowerCase().includes(q) ||
        (tx.tenant_name || "").toLowerCase().includes(q)
      );
    }
    if (txFilterDateFrom) {
      filtered = filtered.filter(tx => tx.created_at >= txFilterDateFrom);
    }
    if (txFilterDateTo) {
      const toDate = txFilterDateTo + "T23:59:59";
      filtered = filtered.filter(tx => tx.created_at <= toDate);
    }
    return filtered;
  }, [transactions, txFilterType, txFilterMethod, txFilterSearch, txFilterDateFrom, txFilterDateTo]);

  // Unique payment methods from transactions for filter dropdown
  const paymentMethods = useMemo(() => {
    const methods = new Set();
    transactions.forEach(tx => {
      if (tx.payment_method) methods.add(tx.payment_method);
    });
    return Array.from(methods);
  }, [transactions]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size={32} className="animate-spin text-indigo-600" />
      </div>
    );
  }

  const changePercent = revenue?.prev_month > 0
    ? (((revenue.current_month - revenue.prev_month) / revenue.prev_month) * 100).toFixed(0)
    : revenue?.current_month > 0 ? 100 : 0;
  const isUp = Number(changePercent) >= 0;

  const exportRevenue = () => {
    if (!revenue) return;
    const headers = ["Apartman", "Kiraci Sayisi", "Yukleme Sayisi", "Toplam Gelir (TL)"];
    const rows = (revenue.by_apartment || []).map(a => [a.apartment_name, a.tenant_count, a.topup_count, a.amount.toFixed(2)]);
    rows.push(["TOPLAM", "", "", (revenue.current_month || 0).toFixed(2)]);
    downloadCSV(`gelir_raporu_${period}.csv`, headers, rows);
  };

  const exportTenantRevenue = () => {
    if (!revenue) return;
    const headers = ["Kiraci", "Apartman", "Daire", "Yukleme Sayisi", "Toplam (TL)"];
    const rows = (revenue.by_tenant || []).map(t => [t.tenant_name, t.apartment_name, t.unit_name, t.count, t.amount.toFixed(2)]);
    downloadCSV(`kiraci_gelir_raporu_${period}.csv`, headers, rows);
  };

  const exportTransactions = () => {
    const headers = ["Tarih", "Tur", "Aciklama", "Tutar (TL)", "kWh", "Odeme Yontemi"];
    const rows = filteredTransactions.map(tx => [
      new Date(tx.created_at).toLocaleString("tr-TR"),
      tx.type === "topup" ? "Yukleme" : tx.type === "consumption" ? "Tuketim" : tx.type === "kwh_topup" ? "kWh Yukleme" : tx.type,
      tx.description || "-",
      (tx.tl_amount || tx.amount || 0).toFixed(2),
      tx.kwh_amount ? tx.kwh_amount.toFixed(2) : "-",
      tx.payment_method === "cash" ? "Nakit" : tx.payment_method === "bank" ? "Havale" : tx.payment_method || "-",
    ]);
    downloadCSV(`islem_gecmisi_${period}.csv`, headers, rows);
  };

  const exportConsumption = () => {
    const headers = ["Tarih", "Tuketim (kWh)", "Maliyet (TL)", "Yukleme (TL)"];
    const rows = chartData.map(d => [d.label, d.kwh, (d.cost || 0).toFixed(2), (d.topup || 0).toFixed(2)]);
    downloadCSV(`tuketim_raporu_${period}.csv`, headers, rows);
  };

  const exportDebt = () => {
    if (!debtData?.tenants?.length) return;
    const headers = ["Kiraci", "Apartman", "Daire", "Bakiye (TL)", "Fatura Borcu (TL)", "Toplam Borc (TL)", "Odenmemis Fatura"];
    const rows = debtData.tenants.map(t => [t.tenant_name, t.apartment_name, t.unit_name, t.balance.toFixed(2), t.invoice_debt.toFixed(2), t.total_debt.toFixed(2), t.unpaid_invoices]);
    downloadCSV("borc_takip_raporu.csv", headers, rows);
  };

  const methodLabel = (m) => {
    const labels = { cash: "Nakit", bank: "Havale/EFT", auto_deduct: "Otomatik", card: "Kredi Karti", manual: "Manuel" };
    return labels[m] || m;
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900">
            {isTenant ? "Tüketim Raporu" : "Raporlar"}
          </h1>
          <p className="text-slate-500 mt-1">
            {isTenant ? "Tüketim ve işlem geçmişinizi analiz edin" : "Gelir, tüketim ve işlem verilerinizi analiz edin"}
          </p>
          <span className="inline-flex items-center text-xs text-slate-400 mt-1" data-testid="retention-info">
            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
            Son 360 gün gösteriliyor
          </span>
        </div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <Select value={period} onValueChange={(v) => { setPeriod(v); if (v !== "custom") { setCustomFrom(""); setCustomTo(""); } }}>
            <SelectTrigger className="w-40" data-testid="period-select">
              <Calendar size={18} className="mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="day">Bugün</SelectItem>
              <SelectItem value="week">Bu Hafta</SelectItem>
              <SelectItem value="month">Bu Ay</SelectItem>
              <SelectItem value="year">Bu Yıl</SelectItem>
              <SelectItem value="custom">Özel Aralık</SelectItem>
            </SelectContent>
          </Select>
          {period === "custom" && (
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                className="w-36"
                data-testid="custom-date-from"
                max={customTo || undefined}
              />
              <span className="text-slate-400 text-sm">-</span>
              <Input
                type="date"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                className="w-36"
                data-testid="custom-date-to"
                min={customFrom || undefined}
                max={new Date().toISOString().slice(0, 10)}
              />
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      {!isTenant ? (
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4 max-w-lg">
            <TabsTrigger value="revenue" data-testid="tab-revenue">
              <CurrencyCircleDollar size={16} className="mr-1.5" /> Gelir
            </TabsTrigger>
            <TabsTrigger value="consumption" data-testid="tab-consumption">
              <Lightning size={16} className="mr-1.5" /> Tüketim
            </TabsTrigger>
            <TabsTrigger value="transactions" data-testid="tab-transactions">
              <Receipt size={16} className="mr-1.5" /> İşlemler
            </TabsTrigger>
            <TabsTrigger value="debt" data-testid="tab-debt">
              <Warning size={16} className="mr-1.5" /> Borç Takip
            </TabsTrigger>
          </TabsList>

          {/* ─── REVENUE TAB ─── */}
          <TabsContent value="revenue" className="space-y-6 mt-6">
            {/* Export Buttons */}
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={exportRevenue} data-testid="export-revenue-btn" disabled={!revenue?.by_apartment?.length}>
                <Download size={14} className="mr-1.5" /> Gelir Raporu
              </Button>
              <Button variant="outline" size="sm" onClick={exportTenantRevenue} data-testid="export-tenant-revenue-btn" disabled={!revenue?.by_tenant?.length}>
                <Download size={14} className="mr-1.5" /> Kiraci Raporu
              </Button>
            </div>
            {/* Revenue Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="border-slate-200 shadow-sm" data-testid="revenue-current-month">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-500">Bu Ay Gelir</p>
                      <p className="text-3xl font-bold text-slate-900 mt-1">
                        {"\u20BA"}{(revenue?.current_month || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                      </p>
                      <div className="flex items-center gap-1 mt-2">
                        {isUp ? (
                          <TrendUp size={16} className="text-emerald-500" />
                        ) : (
                          <TrendDown size={16} className="text-red-500" />
                        )}
                        <span className={`text-xs font-medium ${isUp ? "text-emerald-600" : "text-red-600"}`}>
                          %{Math.abs(changePercent)} {isUp ? "artış" : "düşüş"}
                        </span>
                        <span className="text-xs text-slate-400">geçen aya göre</span>
                      </div>
                    </div>
                    <div className="w-14 h-14 bg-emerald-50 rounded-2xl flex items-center justify-center">
                      <CurrencyCircleDollar size={28} weight="duotone" className="text-emerald-600" />
                    </div>
                  </div>
                  <p className="text-xs text-slate-400 mt-3">{revenue?.current_month_count || 0} yükleme işlemi</p>
                </CardContent>
              </Card>

              <Card className="border-slate-200 shadow-sm" data-testid="revenue-prev-month">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-500">Geçen Ay Gelir</p>
                      <p className="text-3xl font-bold text-slate-900 mt-1">
                        {"\u20BA"}{(revenue?.prev_month || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                      </p>
                    </div>
                    <div className="w-14 h-14 bg-slate-100 rounded-2xl flex items-center justify-center">
                      <Wallet size={28} weight="duotone" className="text-slate-500" />
                    </div>
                  </div>
                  <p className="text-xs text-slate-400 mt-3">{revenue?.prev_month_count || 0} yükleme işlemi</p>
                </CardContent>
              </Card>

              <Card className="border-slate-200 shadow-sm" data-testid="revenue-yearly">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-500">Yıllık Toplam</p>
                      <p className="text-3xl font-bold text-slate-900 mt-1">
                        {"\u20BA"}{(revenue?.yearly_total || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                      </p>
                    </div>
                    <div className="w-14 h-14 bg-indigo-50 rounded-2xl flex items-center justify-center">
                      <ChartLineUp size={28} weight="duotone" className="text-indigo-600" />
                    </div>
                  </div>
                  <p className="text-xs text-slate-400 mt-3">{new Date().getFullYear()} yılı toplamı</p>
                </CardContent>
              </Card>
            </div>

            {/* Charts Row: Monthly Trend + Payment Method Pie */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Monthly Trend */}
              <Card className="border-slate-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg font-heading" data-testid="revenue-trend-title">
                    Aylık Gelir Trendi
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64" data-testid="revenue-trend-chart">
                    {(revenue?.monthly_trend || []).length === 0 ? (
                      <div className="flex items-center justify-center h-full text-slate-400">
                        <p>Henüz gelir verisi yok</p>
                      </div>
                    ) : (
                      <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                        <BarChart data={revenue.monthly_trend}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                          <XAxis dataKey="label" stroke="#94A3B8" fontSize={12} />
                          <YAxis stroke="#94A3B8" fontSize={12} tickFormatter={(v) => `₺${v}`} />
                          <Tooltip
                            contentStyle={{ backgroundColor: "#fff", border: "1px solid #E2E8F0", borderRadius: "12px", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)" }}
                            formatter={(value) => [`₺${Number(value).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}`, revenueLabel]}
                          />
                          <Bar dataKey="amount" fill="#10B981" radius={[6, 6, 0, 0]} name="Gelir" />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Payment Method Pie Chart */}
              <Card className="border-slate-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg font-heading" data-testid="payment-method-pie-title">
                    Ödeme Yöntemi Dağılımı
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64" data-testid="payment-method-pie-chart">
                    {(revenue?.by_payment_method || []).length === 0 ? (
                      <div className="flex items-center justify-center h-full text-slate-400">
                        <p>Bu dönem ödeme verisi yok</p>
                      </div>
                    ) : (
                      <div className="flex items-center gap-4 h-full">
                        <ResponsiveContainer width="55%" height="100%" minWidth={0}>
                          <PieChart>
                            <Pie
                              data={revenue.by_payment_method}
                              dataKey="amount"
                              nameKey="label"
                              cx="50%"
                              cy="50%"
                              outerRadius={80}
                              innerRadius={40}
                              paddingAngle={2}
                            >
                              {revenue.by_payment_method.map((_, idx) => (
                                <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(value) => `₺${Number(value).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}`} />
                          </PieChart>
                        </ResponsiveContainer>
                        <div className="flex-1 space-y-3">
                          {revenue.by_payment_method.map((m, idx) => {
                            const total = revenue.by_payment_method.reduce((s, x) => s + x.amount, 0);
                            const pct = total > 0 ? ((m.amount / total) * 100).toFixed(1) : 0;
                            return (
                              <div key={idx} className="space-y-1" data-testid={`payment-method-${m.method}`}>
                                <div className="flex items-center justify-between text-sm">
                                  <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: PIE_COLORS[idx % PIE_COLORS.length] }} />
                                    <span className="text-slate-700">{m.label}</span>
                                  </div>
                                  <span className="font-medium text-slate-900">%{pct}</span>
                                </div>
                                <div className="flex items-center justify-between text-xs text-slate-500 pl-5">
                                  <span>{"\u20BA"}{m.amount.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}</span>
                                  <span>{m.count} işlem</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Apartment Pie + Table */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="border-slate-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg font-heading" data-testid="revenue-apt-title">
                    Apartman Bazlı Gelir
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64" data-testid="revenue-apt-chart">
                    {(revenue?.by_apartment || []).length === 0 ? (
                      <div className="flex items-center justify-center h-full text-slate-400">
                        <p>Bu ay gelir verisi yok</p>
                      </div>
                    ) : (
                      <div className="flex items-center gap-4 h-full">
                        <ResponsiveContainer width="55%" height="100%" minWidth={0}>
                          <PieChart>
                            <Pie
                              data={revenue.by_apartment}
                              dataKey="amount"
                              nameKey="apartment_name"
                              cx="50%"
                              cy="50%"
                              outerRadius={80}
                              innerRadius={40}
                              paddingAngle={2}
                            >
                              {revenue.by_apartment.map((_, idx) => (
                                <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(value) => `₺${Number(value).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}`} />
                          </PieChart>
                        </ResponsiveContainer>
                        <div className="flex-1 space-y-2">
                          {revenue.by_apartment.map((apt, idx) => (
                            <div key={idx} className="flex items-center justify-between text-sm">
                              <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: PIE_COLORS[idx % PIE_COLORS.length] }} />
                                <span className="text-slate-700 truncate max-w-[120px]">{apt.apartment_name}</span>
                              </div>
                              <span className="font-medium text-slate-900">{"\u20BA"}{apt.amount.toLocaleString("tr-TR")}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Top 10 Consumers */}
              <Card className="border-slate-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg font-heading flex items-center gap-2" data-testid="top-consumers-title">
                    <Lightning size={20} className="text-amber-500" /> En Çok Tüketen 10 Kiracı
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {topConsumers.length === 0 ? (
                    <div className="flex items-center justify-center h-48 text-slate-400">
                      <p>Bu dönem tüketim verisi yok</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-8">#</TableHead>
                            <TableHead>Kiracı</TableHead>
                            <TableHead>Apartman</TableHead>
                            <TableHead className="text-right">kWh</TableHead>
                            <TableHead className="text-right">Tutar</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {topConsumers.map((tc, idx) => (
                            <TableRow key={tc.tenant_id} data-testid={`top-consumer-row-${idx}`}>
                              <TableCell>
                                <Badge variant={idx < 3 ? "default" : "outline"} className={idx < 3 ? "bg-amber-500 text-white text-xs" : "text-xs"}>
                                  {idx + 1}
                                </Badge>
                              </TableCell>
                              <TableCell className="font-medium">
                                {tc.tenant_name}
                                <span className="block text-xs text-slate-400">{tc.unit_name}</span>
                              </TableCell>
                              <TableCell className="text-slate-500 text-sm">{tc.apartment_name}</TableCell>
                              <TableCell className="text-right font-semibold text-indigo-600">{tc.total_kwh.toFixed(2)}</TableCell>
                              <TableCell className="text-right font-medium">{"\u20BA"}{tc.total_tl.toFixed(2)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Apartment Detail Table */}
            {(revenue?.by_apartment || []).length > 0 && (
              <Card className="border-slate-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg font-heading flex items-center gap-2">
                    <Buildings size={20} className="text-indigo-600" /> Apartman Detaylı Gelir
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Apartman</TableHead>
                          <TableHead className="text-center">Kiracı Sayısı</TableHead>
                          <TableHead className="text-center">Yükleme Sayısı</TableHead>
                          <TableHead className="text-right">Toplam Gelir</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {revenue.by_apartment.map((apt, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-medium">
                              <div className="flex items-center gap-2">
                                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: PIE_COLORS[idx % PIE_COLORS.length] }} />
                                {apt.apartment_name}
                              </div>
                            </TableCell>
                            <TableCell className="text-center">{apt.tenant_count}</TableCell>
                            <TableCell className="text-center">{apt.topup_count}</TableCell>
                            <TableCell className="text-right font-semibold text-emerald-600">
                              {"\u20BA"}{apt.amount.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Tenant Revenue Table */}
            {(revenue?.by_tenant || []).length > 0 && (
              <Card className="border-slate-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg font-heading flex items-center gap-2">
                    <Users size={20} className="text-indigo-600" /> Kiracı Bazlı Gelir
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Kiracı</TableHead>
                          <TableHead>Apartman</TableHead>
                          <TableHead>Daire</TableHead>
                          <TableHead className="text-center">Yükleme</TableHead>
                          <TableHead className="text-right">Tutar</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {revenue.by_tenant.map((t, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-medium">{t.tenant_name}</TableCell>
                            <TableCell className="text-slate-500">{t.apartment_name}</TableCell>
                            <TableCell className="text-slate-500">{t.unit_name}</TableCell>
                            <TableCell className="text-center">
                              <Badge variant="outline" className="text-xs">{t.count}</Badge>
                            </TableCell>
                            <TableCell className="text-right font-semibold text-emerald-600">
                              {"\u20BA"}{t.amount.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Staff Performance */}
            {staffPerformance.length > 0 && (
              <Card className="border-slate-200 shadow-sm">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-lg font-heading flex items-center gap-2">
                    <Users size={20} className="text-indigo-600" /> Personel Tahsilat Performansı
                  </CardTitle>
                  <Button variant="outline" size="sm" onClick={() => {
                    const headers = ["Personel", "Rol", "Toplam (TL)", "Islem", "Nakit (TL)", "Nakit Islem", "Havale (TL)", "Havale Islem"];
                    const rows = staffPerformance.map(s => [s.staff_name, s.role, s.total_amount.toFixed(2), s.count, s.cash_amount.toFixed(2), s.cash_count, s.bank_amount.toFixed(2), s.bank_count]);
                    downloadCSV("personel_performans.csv", headers, rows);
                  }} data-testid="export-staff-performance-btn">
                    <Download size={14} className="mr-1.5" /> CSV
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Personel</TableHead>
                          <TableHead className="text-center">İşlem</TableHead>
                          <TableHead className="text-right">Toplam</TableHead>
                          <TableHead className="text-right">Nakit</TableHead>
                          <TableHead className="text-right">Havale</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {staffPerformance.map((s, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-medium">{s.staff_name}</TableCell>
                            <TableCell className="text-center">
                              <Badge variant="outline" className="text-xs">{s.count}</Badge>
                            </TableCell>
                            <TableCell className="text-right font-bold">{"\u20BA"}{s.total_amount.toFixed(2)}</TableCell>
                            <TableCell className="text-right text-emerald-600">
                              {"\u20BA"}{s.cash_amount.toFixed(2)} <span className="text-xs text-slate-400">({s.cash_count})</span>
                            </TableCell>
                            <TableCell className="text-right text-indigo-600">
                              {"\u20BA"}{s.bank_amount.toFixed(2)} <span className="text-xs text-slate-400">({s.bank_count})</span>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* ─── CONSUMPTION TAB ─── */}
          <TabsContent value="consumption" className="space-y-6 mt-6">
            <div className="flex justify-end">
              <Button variant="outline" size="sm" onClick={exportConsumption} data-testid="export-consumption-btn" disabled={!chartData.length}>
                <Download size={14} className="mr-1.5" /> CSV İndir
              </Button>
            </div>
            <ConsumptionSection consumption={consumption} chartData={chartData} periodLabels={periodLabels} period={period} consumptionLabel={consumptionLabel} topupLabel={topupLabel} />
          </TabsContent>

          {/* ─── TRANSACTIONS TAB ─── */}
          <TabsContent value="transactions" className="space-y-6 mt-6">
            <div className="flex flex-col sm:flex-row justify-between gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <div className="relative">
                  <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Kiracı veya açıklama ara..."
                    value={txFilterSearch}
                    onChange={(e) => { setTxFilterSearch(e.target.value); setTxPage(1); }}
                    className="pl-9 w-56"
                    data-testid="tx-filter-search"
                  />
                </div>
                <Select value={txFilterType} onValueChange={(v) => { setTxFilterType(v); setTxPage(1); }}>
                  <SelectTrigger className="w-36" data-testid="tx-filter-type">
                    <Funnel size={14} className="mr-1.5" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tüm Türler</SelectItem>
                    <SelectItem value="topup">Yükleme</SelectItem>
                    <SelectItem value="consumption">Tüketim</SelectItem>
                    <SelectItem value="kwh_topup">kWh Yükleme</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={txFilterMethod} onValueChange={(v) => { setTxFilterMethod(v); setTxPage(1); }}>
                  <SelectTrigger className="w-36" data-testid="tx-filter-method">
                    <Wallet size={14} className="mr-1.5" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tüm Yöntemler</SelectItem>
                    {paymentMethods.map(m => (
                      <SelectItem key={m} value={m}>{methodLabel(m)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  type="date"
                  value={txFilterDateFrom}
                  onChange={(e) => { setTxFilterDateFrom(e.target.value); setTxPage(1); }}
                  className="w-36"
                  data-testid="tx-filter-date-from"
                  placeholder="Başlangıç"
                />
                <Input
                  type="date"
                  value={txFilterDateTo}
                  onChange={(e) => { setTxFilterDateTo(e.target.value); setTxPage(1); }}
                  className="w-36"
                  data-testid="tx-filter-date-to"
                  placeholder="Bitiş"
                />
                {(txFilterType !== "all" || txFilterMethod !== "all" || txFilterSearch || txFilterDateFrom || txFilterDateTo) && (
                  <Button variant="ghost" size="sm" onClick={() => {
                    setTxFilterType("all"); setTxFilterMethod("all"); setTxFilterSearch(""); setTxFilterDateFrom(""); setTxFilterDateTo(""); setTxPage(1);
                  }} data-testid="tx-filter-clear">
                    Temizle
                  </Button>
                )}
              </div>
              <Button variant="outline" size="sm" onClick={exportTransactions} data-testid="export-transactions-btn" disabled={!filteredTransactions.length}>
                <Download size={14} className="mr-1.5" /> CSV İndir
              </Button>
            </div>
            {filteredTransactions.length !== transactions.length && (
              <p className="text-sm text-slate-500" data-testid="tx-filter-count">
                {filteredTransactions.length} / {transactions.length} işlem gösteriliyor
              </p>
            )}
            <TransactionsSection transactions={filteredTransactions} txPage={txPage} txPageSize={txPageSize} setTxPage={setTxPage} setTxPageSize={setTxPageSize} />
          </TabsContent>

          {/* ─── DEBT TRACKING TAB ─── */}
          <TabsContent value="debt" className="space-y-6 mt-6">
            <DebtTrackingSection debtData={debtData} exportDebt={exportDebt} />
          </TabsContent>
        </Tabs>
      ) : (
        // Tenant view: no tabs, just consumption + transactions
        <div className="space-y-6">
          <ConsumptionSection consumption={consumption} chartData={chartData} periodLabels={periodLabels} period={period} consumptionLabel={consumptionLabel} topupLabel={topupLabel} />
          <TransactionsSection transactions={transactions} txPage={txPage} txPageSize={txPageSize} setTxPage={setTxPage} setTxPageSize={setTxPageSize} />
        </div>
      )}
    </div>
  );
};

// --- Consumption Sub-component ---
const ConsumptionSection = ({ consumption, chartData, periodLabels, period, consumptionLabel, topupLabel }) => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500" data-testid="stat-consumption-label">
                {periodLabels[period]} Tüketim
              </p>
              <p className="text-3xl font-bold text-slate-900 mt-1" data-testid="stat-consumption-value">
                {consumption?.total_kwh ?? 0}
                <span className="text-lg text-slate-400 font-normal ml-1">kWh</span>
              </p>
            </div>
            <div className="w-14 h-14 bg-indigo-50 rounded-2xl flex items-center justify-center">
              <Lightning size={28} weight="duotone" className="text-indigo-600" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500" data-testid="stat-topup-label">
                {periodLabels[period]} Yükleme
              </p>
              <p className="text-3xl font-bold text-slate-900 mt-1" data-testid="stat-topup-value">
                {"\u20BA"}{chartData.reduce((s, d) => s + (d.topup || 0), 0).toFixed(2)}
              </p>
            </div>
            <div className="w-14 h-14 bg-amber-50 rounded-2xl flex items-center justify-center">
              <ChartLineUp size={28} weight="duotone" className="text-amber-600" />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>

    <div className="grid grid-cols-1 gap-6">
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg font-heading" data-testid="consumption-chart-title">
            Tüketim Grafiği (kWh)
            <span className="inline-flex items-center text-xs font-normal text-slate-400 ml-2">
              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
              Son 360 gün
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64" data-testid="consumption-chart">
            {chartData.length === 0 ? (
              <div className="flex items-center justify-center h-full text-slate-400">
                <p>Bu dönem için tüketim verisi bulunamadı</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="label" stroke="#94A3B8" fontSize={12} />
                  <YAxis stroke="#94A3B8" fontSize={12} />
                  <Tooltip
                    contentStyle={{ backgroundColor: "#fff", border: "1px solid #E2E8F0", borderRadius: "12px", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)" }}
                    formatter={(value, name) => {
                      const labels = { kwh: consumptionLabel, topup: topupLabel };
                      const suffix = name === "kwh" ? " kWh" : " ₺";
                      return [`${value}${suffix}`, labels[name] || name];
                    }}
                  />
                  <Legend formatter={(v) => (v === "kwh" ? "Tüketim (kWh)" : "Yükleme (₺)")} />
                  <Bar dataKey="kwh" fill="#4F46E5" radius={[4, 4, 0, 0]} name="kwh" />
                  <Bar dataKey="topup" fill="#10B981" radius={[4, 4, 0, 0]} name="topup" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  </div>
);

// --- Transactions Sub-component ---
const TransactionsSection = ({ transactions, txPage, txPageSize, setTxPage, setTxPageSize }) => (
  <Card className="border-slate-200 shadow-sm">
    <CardHeader>
      <CardTitle className="text-lg font-heading">
        İşlem Geçmişi
        <span className="inline-flex items-center text-xs font-normal text-slate-400 ml-2">
          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
          Son 360 gün
        </span>
      </CardTitle>
    </CardHeader>
    <CardContent>
      {transactions.length === 0 ? (
        <div className="py-12 text-center">
          <Receipt size={48} className="mx-auto text-slate-300 mb-4" />
          <p className="text-slate-500">Henüz işlem kaydı yok</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tarih</TableHead>
                <TableHead>Tür</TableHead>
                <TableHead>Açıklama</TableHead>
                <TableHead className="text-right">Tutar</TableHead>
                <TableHead className="text-right hidden md:table-cell">kWh</TableHead>
                <TableHead className="text-right hidden md:table-cell">Ödeme</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {transactions.slice((txPage - 1) * txPageSize, txPage * txPageSize).map((tx) => {
                const typeLabel = tx.type === "topup" ? "Yükleme" : tx.type === "consumption" ? "Tüketim" : tx.type === "kwh_topup" ? "kWh Yükleme" : tx.type;
                const typeColor = tx.type === "topup" || tx.type === "kwh_topup" ? "bg-emerald-100 text-emerald-700" : tx.type === "consumption" ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-700";
                return (
                  <TableRow key={tx.id} data-testid={`transaction-row-${tx.id}`}>
                    <TableCell className="whitespace-nowrap text-sm text-slate-600">
                      {new Date(tx.created_at).toLocaleDateString("tr-TR")}
                      <span className="block text-xs text-slate-400">
                        {new Date(tx.created_at).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge className={`text-xs ${typeColor}`}>{typeLabel}</Badge>
                    </TableCell>
                    <TableCell className="max-w-[300px] truncate text-sm">
                      {tx.description || "-"}
                      {tx.tenant_name && (
                        <span className="block text-xs text-slate-400">{tx.tenant_name}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <span className={`font-semibold ${tx.type === "consumption" ? "text-red-600" : "text-emerald-600"}`}>
                        {tx.type === "consumption" ? "-" : "+"}{"\u20BA"}{Math.abs(tx.tl_amount || tx.amount || 0).toFixed(2)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right hidden md:table-cell text-sm text-slate-600">
                      {tx.kwh_amount ? `${tx.kwh_amount.toFixed(2)} kWh` : "-"}
                    </TableCell>
                    <TableCell className="text-right hidden md:table-cell">
                      {tx.payment_method ? (
                        <Badge variant="outline" className="text-xs">
                          {tx.payment_method === "cash" ? "Nakit" : tx.payment_method === "bank" ? "Havale" : tx.payment_method === "auto_deduct" ? "Otomatik" : tx.payment_method}
                        </Badge>
                      ) : "-"}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
      {transactions.length > txPageSize && (
        <div className="mt-4">
          <Pagination total={transactions.length} page={txPage} pageSize={txPageSize}
            onPageChange={setTxPage} onPageSizeChange={setTxPageSize} />
        </div>
      )}
    </CardContent>
  </Card>
);

// --- Debt Tracking Sub-component ---
const DebtTrackingSection = ({ debtData, exportDebt }) => {
  if (!debtData) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size={32} className="animate-spin text-indigo-600" />
      </div>
    );
  }

  const { summary, tenants } = debtData;

  return (
    <div className="space-y-6">
      {/* Export */}
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={exportDebt} data-testid="export-debt-btn" disabled={!tenants?.length}>
          <Download size={14} className="mr-1.5" /> CSV İndir
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-red-200 shadow-sm bg-red-50/30" data-testid="debt-total-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-red-600">Toplam Borç</p>
                <p className="text-3xl font-bold text-red-700 mt-1">
                  {"\u20BA"}{(summary?.total_debt || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                </p>
              </div>
              <div className="w-14 h-14 bg-red-100 rounded-2xl flex items-center justify-center">
                <Warning size={28} weight="duotone" className="text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-amber-200 shadow-sm bg-amber-50/30" data-testid="debt-debtors-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-amber-600">Borçlu Kiracı</p>
                <p className="text-3xl font-bold text-amber-700 mt-1">
                  {summary?.debtors_count || 0}
                </p>
              </div>
              <div className="w-14 h-14 bg-amber-100 rounded-2xl flex items-center justify-center">
                <Users size={28} weight="duotone" className="text-amber-600" />
              </div>
            </div>
            <p className="text-xs text-amber-500 mt-3">Toplam {summary?.total_tenants || 0} kiracıdan</p>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm" data-testid="debt-ratio-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Borç Oranı</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">
                  %{summary?.total_tenants > 0 ? ((summary.debtors_count / summary.total_tenants) * 100).toFixed(1) : 0}
                </p>
              </div>
              <div className="w-14 h-14 bg-slate-100 rounded-2xl flex items-center justify-center">
                <ChartLineUp size={28} weight="duotone" className="text-slate-500" />
              </div>
            </div>
            <p className="text-xs text-slate-400 mt-3">Borçlu kiracı yüzdesi</p>
          </CardContent>
        </Card>
      </div>

      {/* Debt Table */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg font-heading flex items-center gap-2">
            <Warning size={20} className="text-red-500" /> Borçlu Kiracılar
          </CardTitle>
        </CardHeader>
        <CardContent>
          {(!tenants || tenants.length === 0) ? (
            <div className="py-12 text-center">
              <Warning size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">Borçlu kiracı bulunmuyor</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Kiracı</TableHead>
                    <TableHead>Apartman</TableHead>
                    <TableHead>Daire</TableHead>
                    <TableHead className="text-right">Bakiye</TableHead>
                    <TableHead className="text-right">Fatura Borcu</TableHead>
                    <TableHead className="text-right">Toplam Borç</TableHead>
                    <TableHead className="text-center">Ödenmemiş Fatura</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tenants.map((t) => (
                    <TableRow key={t.tenant_id} data-testid={`debt-row-${t.tenant_id}`}>
                      <TableCell className="font-medium">
                        {t.tenant_name}
                        <span className="block text-xs text-slate-400">{t.email}</span>
                      </TableCell>
                      <TableCell className="text-slate-500">{t.apartment_name}</TableCell>
                      <TableCell className="text-slate-500">{t.unit_name}</TableCell>
                      <TableCell className="text-right">
                        <span className={t.balance < 0 ? "text-red-600 font-semibold" : "text-slate-600"}>
                          {"\u20BA"}{t.balance.toFixed(2)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={t.invoice_debt > 0 ? "text-red-600 font-semibold" : "text-slate-600"}>
                          {"\u20BA"}{t.invoice_debt.toFixed(2)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="text-red-700 font-bold">
                          {"\u20BA"}{t.total_debt.toFixed(2)}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        {t.unpaid_invoices > 0 ? (
                          <Badge variant="destructive" className="text-xs">{t.unpaid_invoices}</Badge>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ReportsPage;
